import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Brand from "@/components/Brand";
import { ArrowLeft, ArrowRight, Upload, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";

const STEPS = ["Contact", "Project", "References", "Account"];

export default function Intake() {
  const navigate = useNavigate();
  const { setAuthFromIntake } = useAuth();
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    project_description: "",
    placement: "",
    size: "",
    budget: "",
    password: "",
  });
  const [files, setFiles] = useState([]); // {file, preview}

  const update = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const handleFiles = (e) => {
    const list = Array.from(e.target.files || []);
    const next = list.map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
    setFiles((prev) => [...prev, ...next].slice(0, 8));
  };
  const removeFile = (idx) => setFiles((prev) => prev.filter((_, i) => i !== idx));

  const stepValid = () => {
    if (step === 0) return form.name && form.email && form.phone;
    if (step === 1) return form.project_description && form.placement && form.size;
    if (step === 2) return true;
    if (step === 3) return form.password.length >= 8;
    return false;
  };

  const next = () => {
    setErr("");
    if (!stepValid()) {
      setErr("Please complete the required fields.");
      return;
    }
    setStep((s) => Math.min(STEPS.length - 1, s + 1));
  };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = async () => {
    setBusy(true);
    setErr("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v || ""));
      files.forEach(({ file }) => fd.append("files", file));
      const { data } = await http.post("/intake/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const userResp = await http.get("/auth/me");
      setAuthFromIntake(userResp.data, data.access_token);
      setSubmitted(true);  // show confirmation screen instead of bouncing to dashboard
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  if (submitted) {
    return <IntakeConfirmation name={form.name} onContinue={() => navigate("/dashboard")} />;
  }

  return (
    <div className="min-h-screen bg-[#141C16]">
      {/* Header */}
      <div className="border-b border-[#D2A853]/15 px-6 lg:px-12 py-5 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-[#A3A8A4] hover:text-[#D2A853]" data-testid="intake-back-home">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.4} />
          <span className="overline">Home</span>
        </Link>
        <Brand variant="submark" className="h-9 w-9" />
        <div className="w-16" />
      </div>

      <div className="max-w-3xl mx-auto px-6 lg:px-12 py-12 lg:py-16">
        <div className="text-center mb-12">
          <span className="overline text-[#D2A853]">Step {step + 1} of {STEPS.length}</span>
          <h1 className="font-display text-4xl sm:text-5xl text-[#E8E9E3] mt-3">{STEPS[step]}</h1>
        </div>

        {/* Step indicators */}
        <div className="flex items-center justify-center gap-3 mb-12">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-3">
              <div
                className={`w-8 h-8 flex items-center justify-center border ${
                  i <= step ? "border-[#D2A853] text-[#D2A853] bg-[#D2A853]/10" : "border-[#A3A8A4]/30 text-[#A3A8A4]/50"
                }`}
              >
                {i < step ? <Check className="w-4 h-4" strokeWidth={1.4} /> : <span className="text-xs">{i + 1}</span>}
              </div>
              {i < STEPS.length - 1 && <div className={`w-12 h-px ${i < step ? "bg-[#D2A853]" : "bg-[#A3A8A4]/20"}`} />}
            </div>
          ))}
        </div>

        <div className="frame-card p-8 lg:p-12">
          {step === 0 && (
            <div className="space-y-6">
              <Field label="Full Name" required>
                <input className="input-line" value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="intake-name" />
              </Field>
              <Field label="Email" required>
                <input type="email" className="input-line" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="intake-email" />
              </Field>
              <Field label="Phone" required>
                <input className="input-line" placeholder="+1 555 555 5555" value={form.phone} onChange={(e) => update("phone", e.target.value)} data-testid="intake-phone" />
              </Field>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-6">
              <Field label="Project Description" required>
                <textarea
                  rows={5}
                  className="input-line resize-none"
                  placeholder="Tell us about your idea, style, references, and any meaningful context."
                  value={form.project_description}
                  onChange={(e) => update("project_description", e.target.value)}
                  data-testid="intake-description"
                />
              </Field>
              <div className="grid sm:grid-cols-2 gap-6">
                <Field label="Placement" required>
                  <input className="input-line" placeholder="e.g. inner forearm" value={form.placement} onChange={(e) => update("placement", e.target.value)} data-testid="intake-placement" />
                </Field>
                <Field label="Approx. Size" required>
                  <input className="input-line" placeholder='e.g. 4" wide' value={form.size} onChange={(e) => update("size", e.target.value)} data-testid="intake-size" />
                </Field>
              </div>
              <Field label="Budget Range (optional)">
                <input className="input-line" placeholder="$300 – $600" value={form.budget} onChange={(e) => update("budget", e.target.value)} data-testid="intake-budget" />
              </Field>
              <PricingNote />
            </div>
          )}

          {step === 2 && (
            <div>
              <p className="text-[#A3A8A4] text-sm mb-6">Upload up to 8 reference images. Images help Brian understand the style, mood, and direction of your piece.</p>
              <label className="block border border-dashed border-[#D2A853]/40 hover:bg-[#D2A853]/5 cursor-pointer p-12 text-center transition-colors" data-testid="intake-dropzone">
                <Upload className="w-8 h-8 mx-auto text-[#D2A853] mb-3" strokeWidth={1.4} />
                <span className="overline text-[#D2A853]">Click to Upload Images</span>
                <p className="text-xs text-[#A3A8A4]/70 mt-2">JPG, PNG, WEBP — up to 8 files</p>
                <input type="file" accept="image/*" multiple className="hidden" onChange={handleFiles} data-testid="intake-file-input" />
              </label>
              {files.length > 0 && (
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-3 mt-6">
                  {files.map((f, i) => (
                    <div key={i} className="relative aspect-square border border-[#D2A853]/30">
                      <img src={f.preview} alt="" className="w-full h-full object-cover" />
                      <button
                        onClick={() => removeFile(i)}
                        className="absolute top-1 right-1 bg-[#0A120E]/80 p-1 hover:bg-[#CF6679]/80"
                        data-testid={`intake-remove-file-${i}`}
                      >
                        <X className="w-3 h-3 text-[#E8E9E3]" />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6">
              <p className="text-[#A3A8A4] text-sm">Create a password so you can return to your portal anytime to view notes, references, and appointment status.</p>
              <Field label="Password (8+ characters)" required>
                <input type="password" className="input-line" value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="intake-password" />
              </Field>
            </div>
          )}

          {err && <div className="mt-6 text-[#CF6679] text-sm" data-testid="intake-error">{err}</div>}

          <div className="flex items-center justify-between mt-10 pt-8 border-t border-[#D2A853]/15">
            <button
              onClick={back}
              disabled={step === 0 || busy}
              className="flex items-center gap-2 overline text-[#A3A8A4] hover:text-[#D2A853] disabled:opacity-30"
              data-testid="intake-back-btn"
            >
              <ArrowLeft className="w-4 h-4" strokeWidth={1.4} /> Back
            </button>
            {step < STEPS.length - 1 ? (
              <button onClick={next} className="btn-brass flex items-center gap-2" data-testid="intake-next-btn">
                Continue <ArrowRight className="w-4 h-4" strokeWidth={1.6} />
              </button>
            ) : (
              <button onClick={submit} disabled={busy} className="btn-brass flex items-center gap-2" data-testid="intake-submit-btn">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                Submit Inquiry
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <label className="overline text-[#A3A8A4] block mb-2">
        {label} {required && <span className="text-[#D2A853]">*</span>}
      </label>
      {children}
    </div>
  );
}

function PricingNote() {
  // Inline pricing context — sets expectations during intake without
  // pushing pricing into a hard step.
  return (
    <div className="border-l-2 border-[#D2A853]/40 pl-5 py-2 text-sm text-[#A3A8A4] leading-relaxed" data-testid="intake-pricing-note">
      <p>
        My half-day rate is <span className="text-[#D2A853]">$800</span> and full-day rate is <span className="text-[#D2A853]">$1,500</span>.
        If your project falls below that range, my hourly rate is <span className="text-[#D2A853]">$250</span>.
      </p>
      <p className="text-xs mt-2">No payment is required to book a consultation — pricing is finalised after we talk.</p>
    </div>
  );
}

function IntakeConfirmation({ name, onContinue }) {
  return (
    <div className="min-h-screen flex items-center" data-testid="intake-confirmation">
      <div className="max-w-2xl mx-auto w-full px-6 lg:px-12 py-16 text-center">
        <Check className="w-12 h-12 text-[#D2A853] mx-auto mb-6" strokeWidth={1.4} />
        <span className="overline text-[#D2A853]">Consultation Request Received</span>
        <h1 className="font-display text-4xl sm:text-5xl text-[#E8E9E3] mt-3">
          Thank you{name ? `, ${name.split(" ")[0]}` : ""}.
        </h1>
        <p className="text-[#C8CFC9] leading-relaxed mt-6 max-w-lg mx-auto">
          Brian will reach out shortly to set up a time to discuss your project.
          We&rsquo;ll go over your design, size, and final pricing during your consultation.
        </p>
        <p className="text-xs text-[#A3A8A4] leading-relaxed mt-6 max-w-lg mx-auto">
          No deposit is required at this stage. Once we&rsquo;ve talked through the project, I&rsquo;ll
          assign your appointment type and the appropriate retainer.
        </p>
        <div className="mt-12 flex flex-wrap justify-center gap-3">
          <button onClick={onContinue} className="btn-brass inline-flex items-center gap-2" data-testid="intake-continue-portal">
            Go to my client portal <ArrowRight className="w-4 h-4" />
          </button>
          <Link to="/" className="btn-outline-brass inline-flex items-center gap-2" data-testid="intake-confirmation-home">
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
}
